import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicLayout from '../../layouts/PublicLayout';
import PaymentInstructions from '../../components/booking/PaymentInstructions';
import publicBookingApi from '../../services/publicBookingApi';
import { formatDate } from '../../utils/dateFormatter';
import '../../styles/BookingConfirmation.css';
import { vatPercentSuffix } from '../../utils/vatPricing';

/**
 * BookingConfirmation - Booking confirmation page with payment instructions
 * Route: /booking/confirmation/:confirmationCode
 */
const BookingConfirmation = () => {
  const { confirmationCode } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculate number of nights
  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    if (confirmationCode) {
      loadBooking();
    }
  }, [confirmationCode]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await publicBookingApi.getBooking(confirmationCode, 'ar');

      if (response.success) {
        setBooking(response.booking);
      } else {
        setError(response.error || 'فشل في تحميل بيانات الحجز');
      }
    } catch (err) {
      console.error('Error loading booking:', err);
      setError('حدث خطأ في تحميل بيانات الحجز');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <PublicLayout showLanguageSwitcher={false}>
        <div className="confirmation-loading">
          <div className="spinner"></div>
          <p>جاري تحميل تفاصيل الحجز...</p>
        </div>
      </PublicLayout>
    );
  }

  // Error state
  if (error || !booking) {
    return (
      <PublicLayout showLanguageSwitcher={false}>
        <div className="confirmation-error">
          <i className="bi bi-exclamation-circle"></i>
          <h2>عذراً، حدث خطأ</h2>
          <p>{error || 'لم نتمكن من العثور على الحجز'}</p>
          <button onClick={() => navigate('/')} className="btn-back">
            العودة للرئيسية
          </button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout showLanguageSwitcher={false}>
      <div className="booking-confirmation-page">
        <div className="confirmation-container">

          {/* Success Header */}
          <div className="confirmation-header">
            <div className="success-icon">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <h1>تم إنشاء الحجز بنجاح!</h1>
            <p className="confirmation-subtitle">
              رمز الحجز: <span className="confirmation-code">{booking.confirmationCode}</span>
            </p>
          </div>

          {/* Status Alert */}
          <div className="status-alert pending">
            <i className="bi bi-clock-history"></i>
            <div>
              <strong>في انتظار الدفع</strong>
              <p>يُرجى إكمال الدفع لتأكيد حجزك</p>
            </div>
          </div>

          {/* Booking Details */}
          <div className="booking-details-card">
            <h2 className="section-title">تفاصيل الحجز</h2>

            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <i className="bi bi-building"></i>
                  العقار
                </span>
                <span className="detail-value">{booking.property?.name || 'N/A'}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">
                  <i className="bi bi-calendar-check"></i>
                  تاريخ الوصول
                </span>
                <span className="detail-value">{formatDate(booking.checkIn)}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">
                  <i className="bi bi-calendar-x"></i>
                  تاريخ المغادرة
                </span>
                <span className="detail-value">{formatDate(booking.checkOut)}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">
                  <i className="bi bi-moon-stars"></i>
                  عدد الليالي
                </span>
                <span className="detail-value">
                  {calculateNights(booking.checkIn, booking.checkOut)}
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">
                  <i className="bi bi-people"></i>
                  عدد الضيوف
                </span>
                <span className="detail-value">
                  {booking.numberOfGuests}
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">
                  <i className="bi bi-person"></i>
                  اسم الضيف
                </span>
                <span className="detail-value">{booking.guestName}</span>
              </div>
            </div>

            {/* Price Summary */}
            <div className="price-summary">
              <div className="price-row">
                <span>قبل الضريبة</span>
                <span>{Number(booking.rate?.subtotal ?? booking.totalAmount ?? 0).toFixed(2)} ريال</span>
              </div>
              {/* Gated on presence, not truthiness — a zero-rated stay must show its own
                  0.00 line rather than silently losing the row. The percentage comes from
                  the booking's own figures, never from today's default rate. */}
              {booking.rate?.tax != null && (
                <div className="price-row">
                  <span>الضريبة{vatPercentSuffix(booking.rate?.subtotal, booking.rate?.tax)}</span>
                  <span>{Number(booking.rate.tax).toFixed(2)} ريال</span>
                </div>
              )}
              <hr />
              <div className="price-row total">
                <span>المبلغ الإجمالي</span>
                <span>{Number(booking.rate?.total ?? booking.totalAmount ?? 0).toFixed(2)} ريال</span>
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          <PaymentInstructions
            total={booking.rate?.total || booking.totalAmount}
            currency="SAR"
            confirmationCode={booking.confirmationCode}
          />

          {/* Email Confirmation Notice */}
          <div className="email-notice">
            <i className="bi bi-envelope-check"></i>
            <p>
              تم إرسال تفاصيل الحجز إلى بريدك الإلكتروني:{' '}
              <strong>{booking.guestEmail}</strong>
            </p>
          </div>

          {/* Actions */}
          <div className="confirmation-actions">
            <button
              onClick={() => window.print()}
              className="btn-secondary"
            >
              <i className="bi bi-printer"></i>
              طباعة
            </button>
            <button
              onClick={() => navigate(`/booking/${booking.confirmationCode}`)}
              className="btn-primary"
            >
              <i className="bi bi-eye"></i>
              عرض تفاصيل الحجز
            </button>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default BookingConfirmation;
